import type { EntityData } from '@adornis/baseql/entities/types.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { HealthInsurance } from '../../../../healthinsurance/entities/HealthInsurance.js';
import { useMartinusABAC } from '../../../../permissions/client/useMartinusAbac.js';
import type { Person } from '../../../entities/person.js';
import type { UserOnUserPayload } from '../../../UserPolicy.js';
import { AGArbeit } from '../../entities/employee-sections/ag-arbeit.js';
import { AGAuslandBeschaftigung } from '../../entities/employee-sections/ag-auslandbeschaftigung.js';
import { AGBeschaftigung } from '../../entities/employee-sections/ag-beschaftigung.js';
import { VersicherungsstatusKurzfristigBeschaeftigte } from '../../entities/employee-sections/an-geringfuegigebeschaeftigung.js';
import { UserEmployeeDetailsActions } from '../../UserEmployeeDetailsPolicy.js';

const BeschaftigungLine = stateful(
  (props: {
    form: FunctionalForm<EntityData<Person>>;
    beschaftigung: AGBeschaftigung;
    idx: number;
    canEdit: boolean;
    update: () => void;
  }) => {
    const entlohnungsArt = props.beschaftigung.entlohnungsArt;
    return html`
      <div class="grid grid-cols-1 gap-2 rounded-box border border-base-300 p-3">
        <span class="text-sm font-semibold text-base-content/70">${props.idx + 1}.</span>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
          ${DateInput({
            ...(props.form.field as any)(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'beschaeftigungen',
              props.idx,
              'beschaftigungsbeginn',
            ),
            placeholder: 'Beschäftigungsbeginn',
          })}
          ${DropdownSelectionInput({
            ...(props.form.field as any)(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'beschaeftigungen',
              props.idx,
              'entlohnungsArt',
            ),
            placeholder: 'Wie entlohnt',
            selectables: ['geringfügig entlohnt', 'mehr als geringfügig entlohnt'],
            onPicked: value => {
              props.form
                .field(
                  'mitarbeiterDaten',
                  'anGeringfuegigeBeschaeftigung',
                  'beschaeftigungen',
                  props.idx,
                  'entlohnungsArt',
                )
                .onPicked?.(value);
              props.update();
            },
          })}
        </div>
        ${TextInput({
          ...(props.form.field as any)(
            'mitarbeiterDaten',
            'anGeringfuegigeBeschaeftigung',
            'beschaeftigungen',
            props.idx,
            'arbeitgeberMitAdresse',
          ),
          placeholder: 'Arbeitgeber mit Adresse',
        })}
        <div class="grid grid-cols-1 gap-2 md:grid-cols-2 md:items-center">
          ${NumberInput({
            ...(props.form.field as any)(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'beschaeftigungen',
              props.idx,
              'lohn',
            ),
            placeholder: 'Lohn',
            suffix: '€',
          })}
          ${
            entlohnungsArt === 'geringfügig entlohnt'
              ? ToggleInput({
                  ...(props.form.field as any)(
                    'mitarbeiterDaten',
                    'anGeringfuegigeBeschaeftigung',
                    'beschaeftigungen',
                    props.idx,
                    'mitEigenanteilZurRv',
                  ),
                  label: 'mit Eigenanteil zur RV',
                })
              : nothing
          }
        </div>
        ${
          props.canEdit
            ? html`
                <button
                  class="btn btn-ghost btn-sm justify-self-start md:justify-self-end"
                  @click=${() => {
                    props.form.document?.mitarbeiterDaten?.anGeringfuegigeBeschaeftigung?.beschaeftigungen?.splice(
                      props.idx,
                      1,
                    );
                    props.update();
                  }}
                >
                  <span class="icon-[material-symbols--delete] text-lg"></span>
                </button>
              `
            : nothing
        }
      </div>
    `;
  },
);

const ArbeitLine = stateful(
  (props: {
    form: FunctionalForm<EntityData<Person>>;
    arbeit: AGArbeit;
    idx: number;
    canEdit: boolean;
    update: () => void;
  }) => {
    return html`
      <div class="grid grid-cols-1 gap-2 rounded-box border border-base-300 p-3">
        <span class="text-sm font-semibold text-base-content/70">${props.idx + 1}.</span>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
          ${DateInput({
            ...(props.form.field as any)(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'arbeits',
              props.idx,
              'beginBeschaftigungMeldung',
            ),
            placeholder: 'Beginn',
          })}
          ${DateInput({
            ...(props.form.field as any)(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'arbeits',
              props.idx,
              'endeBeschaftigungMeldung',
            ),
            placeholder: 'Ende',
          })}
        </div>
        ${TextInput({
          ...(props.form.field as any)(
            'mitarbeiterDaten',
            'anGeringfuegigeBeschaeftigung',
            'arbeits',
            props.idx,
            'arbeitgeberMitAdresse',
          ),
          placeholder: 'Arbeitgeber mit Adresse',
        })}
        <div class="grid grid-cols-1 gap-2 md:grid-cols-2 md:items-center">
          ${NumberInput({
            ...(props.form.field as any)(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'arbeits',
              props.idx,
              'tatsachlicheArbeitstageInDiesemZeitraum',
            ),
            placeholder: 'Arbeitstage',
          })}
          ${ToggleInput({
            ...(props.form.field as any)(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'arbeits',
              props.idx,
              'monatlichesArbeitsentgeltGroesserAls520Euro',
            ),
            label: '> 520€ / Monat',
          })}
        </div>
        ${
          props.canEdit
            ? html`
                <button
                  class="btn btn-ghost btn-sm justify-self-start md:justify-self-end"
                  @click=${() => {
                    props.form.document?.mitarbeiterDaten?.anGeringfuegigeBeschaeftigung?.arbeits?.splice(props.idx, 1);
                    props.update();
                  }}
                >
                  <span class="icon-[material-symbols--delete] text-lg"></span>
                </button>
              `
            : nothing
        }
      </div>
    `;
  },
);

const AuslandLine = stateful(
  (props: {
    form: FunctionalForm<EntityData<Person>>;
    ausland: AGAuslandBeschaftigung;
    idx: number;
    canEdit: boolean;
    update: () => void;
  }) => {
    return html`
      <div class="grid grid-cols-1 gap-2 rounded-box border border-base-300 p-3">
        <span class="text-sm font-semibold text-base-content/70">${props.idx + 1}.</span>
        <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
          ${DateInput({
            ...(props.form.field as any)(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'auslandbeschaeftigung',
              props.idx,
              'beschaftigungsBeginn',
            ),
            placeholder: 'Beschäftigungsbeginn',
          })}
          ${DateInput({
            ...(props.form.field as any)(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'auslandbeschaeftigung',
              props.idx,
              'beschaftigungsEnde',
            ),
            placeholder: 'Beschäftigungsende',
          })}
        </div>
        ${TextInput({
          ...(props.form.field as any)(
            'mitarbeiterDaten',
            'anGeringfuegigeBeschaeftigung',
            'auslandbeschaeftigung',
            props.idx,
            'arbeitgeberMitAdresse',
          ),
          placeholder: 'Arbeitgeber mit Adresse',
        })}
        ${
          props.canEdit
            ? html`
                <button
                  class="btn btn-ghost btn-sm justify-self-start md:justify-self-end"
                  @click=${() => {
                    props.form.document?.mitarbeiterDaten?.anGeringfuegigeBeschaeftigung?.auslandbeschaeftigung?.splice(
                      props.idx,
                      1,
                    );
                    props.update();
                  }}
                >
                  <span class="icon-[material-symbols--delete] text-lg"></span>
                </button>
              `
            : nothing
        }
      </div>
    `;
  },
);

export const EmployeeSectionGeringfuegigeBeschaeftigungForm = stateful(
  (props: { form: FunctionalForm<EntityData<Person>> }) => {
    const user = useObservable(() => currentUser(), []) as Person | null;
    const [manuelFormUpdate, setManuelFormUpdate] = useState(0);
    const manuallyUpdateForm = () => setManuelFormUpdate(manuelFormUpdate + 1);

    if (!user) return nothing;
    if (!props.form.document?.mitarbeiterDaten) return nothing;

    const anGB = props.form.document.mitarbeiterDaten.anGeringfuegigeBeschaeftigung;
    const gewuenscht = anGB?.geringfuegigeBeschaeftigungGewuenscht ?? false;

    const { can: canEditSections } = useMartinusABAC<UserOnUserPayload>({
      action: UserEmployeeDetailsActions.EDIT_EMPLOYEE_SECTION_DETAILS,
      viewedUser: props.form.document,
    });

    return html`
      <div class="form-grid">
        ${FormGridEntry({
          label: 'Geringfügige Beschäftigung gewünscht?',
          render: ToggleInput({
            ...props.form.field(
              'mitarbeiterDaten',
              'anGeringfuegigeBeschaeftigung',
              'geringfuegigeBeschaeftigungGewuenscht',
            ),
            label: gewuenscht ? 'Geringfügige Beschäftigung gewünscht' : 'Geringfügige Beschäftigung nicht gewünscht',
          }),
        })}
        ${
          gewuenscht
            ? html`
                ${FormGridEntry({
                  label: 'Betriebsnr. abweichender Krankenkasse',
                  labelSubtext: 'Für eAU, AAG, EEL und A1-Antrag',
                  render: EntityPickerInput({
                    ...props.form.field(
                      'mitarbeiterDaten',
                      'anGeringfuegigeBeschaeftigung',
                      'geringfuegigKrankenkasseBetriebsnummer',
                    ),
                    class: HealthInsurance,
                    filter: new AdornisFilter({
                      searchFields: ['bezeichnung'],
                      sortingFields: ['bezeichnung'],
                      sortingDirections: ['desc'],
                    }),
                    selectionSet: { bezeichnung: 1, ort: 1 },
                    renderString: (item: HealthInsurance) => `${item.bezeichnung} (${item.ort})`,
                    placeholder: 'Krankenkasse nicht hinterlegt',
                    placeholderMode: 'hide',
                    clearable: true,
                  }),
                })}
                ${FormGridEntry({
                  label: 'Versicherungsstatus kurzfristig Beschäftigte',
                  render: DropdownSelectionInput({
                    ...props.form.field(
                      'mitarbeiterDaten',
                      'anGeringfuegigeBeschaeftigung',
                      'versicherungsstatusKurzfristigBeschaeftigt',
                    ),
                    placeholder: 'Versicherungsstatus kurzfristig Beschäftigte nicht hinterlegt',
                    placeholderMode: 'hide',
                    selectables: Object.values(VersicherungsstatusKurzfristigBeschaeftigte),
                    clearable: true,
                  }),
                })}
                ${FormGridEntry({
                  label: 'Möchten Sie sich von der Rentenversicherungspflicht befreien lassen?',
                  render: ToggleInput({
                    ...props.form.field(
                      'mitarbeiterDaten',
                      'anGeringfuegigeBeschaeftigung',
                      'befreiungRentenversicherungspflichtGewuenscht',
                    ),
                    label: anGB?.befreiungRentenversicherungspflichtGewuenscht
                      ? 'Rentenversicherungspflicht befreiung gewünscht'
                      : 'Rentenversicherungspflicht befreiung nicht gewünscht',
                  }),
                })}

                <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">Arbeitsuchend gemeldet</h4>
                <p class="col-span-full text-base-content/70">
                  Sind Sie zu Beginn des Beschäftigungsverhältnisses beschäftigungslos und bei der Agentur für Arbeit
                  arbeit- oder ausbildungsuchend gemeldet?
                </p>
                ${FormGridEntry({
                  label: 'Bei der Agentur für Arbeit gemeldet?',
                  render: html`
                    <div class="flex min-w-0 max-w-full flex-nowrap items-center gap-3 overflow-hidden">
                      ${ToggleInput({
                        ...props.form.field('mitarbeiterDaten', 'anGeringfuegigeBeschaeftigung', 'beschaeftigungslos'),
                        label: anGB?.beschaeftigungslos ? 'Ja' : 'Nein',
                      })}
                      ${
                        anGB?.beschaeftigungslos
                          ? html`
                              <span class="shrink-0 text-sm font-semibold text-base-content/70"
                                >Mit Leistungsbezug:</span
                              >
                              ${ToggleInput({
                                ...props.form.field(
                                  'mitarbeiterDaten',
                                  'anGeringfuegigeBeschaeftigung',
                                  'beschaefitgungslosLeistungsbezug',
                                ),
                                label: anGB.beschaefitgungslosLeistungsbezug ? 'Ja' : 'Nein',
                              })}
                            `
                          : nothing
                      }
                    </div>
                  `,
                })}

                <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">
                  Weitere Beschäftigungsverhältnisse
                </h4>
                <p class="col-span-full text-base-content/70">
                  Es besteht/bestehen derzeit ein/mehrere Beschäftigungsverhältnis(se) bei (einem) anderen
                  Arbeitgeber(n).
                </p>
                ${FormGridEntry({
                  label: 'Bestehende(s) Beschäftigungsverhältnis(se)',
                  render: ToggleInput({
                    ...props.form.field('mitarbeiterDaten', 'anGeringfuegigeBeschaeftigung', 'beschaftigung'),
                    label: anGB?.beschaftigung
                      ? 'Ja, ich übe derzeit folgende Beschäftigungen aus'
                      : 'Nein, ich übe derzeit keine Beschäftigungen aus',
                    onPicked: value => {
                      props.form
                        .field('mitarbeiterDaten', 'anGeringfuegigeBeschaeftigung', 'beschaftigung')
                        .onPicked?.(value);
                      if (value && anGB && !anGB.beschaeftigungen?.length) {
                        anGB.beschaeftigungen = [new AGBeschaftigung({})];
                      }
                    },
                  }),
                })}
                ${
                  anGB?.beschaftigung
                    ? html`
                        <div
                          class="col-span-full flex w-full min-w-0 max-w-full flex-col gap-2 pl-4 border-l-2 border-base-300 overflow-hidden"
                        >
                          ${repeat(
                            anGB.beschaeftigungen ?? [],
                            b => b._id,
                            (beschaftigung, i) =>
                              BeschaftigungLine({
                                form: props.form,
                                beschaftigung,
                                idx: i,
                                canEdit: canEditSections,
                                update: manuallyUpdateForm,
                              }),
                          )}
                          ${
                            canEditSections
                              ? html`
                                  <button
                                    type="button"
                                    class="btn btn-dash btn-block justify-start"
                                    @click=${() => {
                                      anGB.beschaeftigungen?.push(new AGBeschaftigung({}));
                                      manuallyUpdateForm();
                                    }}
                                  >
                                    <span class="icon-[material-symbols--add]"></span>Neue Beschäftigung hinzufügen
                                  </button>
                                `
                              : nothing
                          }
                        </div>
                      `
                    : nothing
                }

                <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">
                  Beschäftigungen im laufenden Kalenderjahr
                </h4>
                <p class="col-span-full text-base-content/70">
                  Im laufenden Kalenderjahr habe ich bereits eine/mehrere Beschäftigung(en) ausgeübt oder war als
                  Beschäftigungslose(r) arbeit- bzw. ausbildungsuchend gemeldet.
                </p>
                ${FormGridEntry({
                  label: 'Beschäftigungsverhältnis(se) im laufenden Kalenderjahr',
                  render: ToggleInput({
                    ...props.form.field('mitarbeiterDaten', 'anGeringfuegigeBeschaeftigung', 'arbeit'),
                    label: anGB?.arbeit
                      ? 'Ja, ich hatte folgende Beschäftigungen/Tätigkeiten im laufenden Kalenderjahr'
                      : 'Nein, ich hatte keine Beschäftigungen/Tätigkeiten im laufenden Kalenderjahr',
                    onPicked: value => {
                      props.form.field('mitarbeiterDaten', 'anGeringfuegigeBeschaeftigung', 'arbeit').onPicked?.(value);
                      if (value && anGB && !anGB.arbeits?.length) {
                        anGB.arbeits = [new AGArbeit({})];
                      }
                      manuallyUpdateForm();
                    },
                  }),
                })}
                ${
                  anGB?.arbeit
                    ? html`
                        <div
                          class="col-span-full flex w-full min-w-0 flex-col gap-3 pl-4 border-l-2 border-base-300 overflow-hidden"
                        >
                          ${repeat(
                            anGB.arbeits ?? [],
                            a => a._id,
                            (arbeit, i) =>
                              ArbeitLine({
                                form: props.form,
                                arbeit,
                                idx: i,
                                canEdit: canEditSections,
                                update: manuallyUpdateForm,
                              }),
                          )}
                          ${
                            canEditSections
                              ? html`
                                  <button
                                    type="button"
                                    class="btn btn-dash btn-block justify-start"
                                    @click=${() => {
                                      anGB.arbeits?.push(new AGArbeit({}));
                                      manuallyUpdateForm();
                                    }}
                                  >
                                    <span class="icon-[material-symbols--add]"></span>Neue Beschäftigung hinzufügen
                                  </button>
                                `
                              : nothing
                          }
                        </div>
                      `
                    : nothing
                }

                <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">Auslandsbeschäftigung</h4>
                <p class="col-span-full text-base-content/70">
                  Es besteht derzeit im Ausland ein Beschäftigungsverhältnis bei einem anderen Arbeitgeber bzw. eine
                  selbstständige Tätigkeit.
                </p>
                ${FormGridEntry({
                  label: 'Beschäftigungsverhältnis(se) im Ausland',
                  render: ToggleInput({
                    ...props.form.field('mitarbeiterDaten', 'anGeringfuegigeBeschaeftigung', 'auslandsbeschaeftigung'),
                    label: anGB?.auslandsbeschaeftigung
                      ? 'Ja. Ich übe derzeit folgende Beschäftigungen/Tätigkeiten im Ausland aus'
                      : 'Nein. Ich übe derzeit keine Beschäftigungen/Tätigkeiten im Ausland aus',
                    onPicked: value => {
                      props.form
                        .field('mitarbeiterDaten', 'anGeringfuegigeBeschaeftigung', 'auslandsbeschaeftigung')
                        .onPicked?.(value);
                      if (value && anGB && !anGB.auslandbeschaeftigung?.length) {
                        anGB.auslandbeschaeftigung = [new AGAuslandBeschaftigung({})];
                      }
                      manuallyUpdateForm();
                    },
                  }),
                })}
                ${
                  anGB?.auslandsbeschaeftigung
                    ? html`
                        <div
                          class="col-span-full flex w-full min-w-0 flex-col gap-3 pl-4 border-l-2 border-base-300 overflow-hidden"
                        >
                          ${repeat(
                            anGB.auslandbeschaeftigung ?? [],
                            a => a._id,
                            (ausland, i) =>
                              AuslandLine({
                                form: props.form,
                                ausland,
                                idx: i,
                                canEdit: canEditSections,
                                update: manuallyUpdateForm,
                              }),
                          )}
                          ${
                            canEditSections
                              ? html`
                                  <button
                                    type="button"
                                    class="btn btn-dash btn-block justify-start"
                                    @click=${() => {
                                      anGB.auslandbeschaeftigung?.push(new AGAuslandBeschaftigung({}));
                                      manuallyUpdateForm();
                                    }}
                                  >
                                    <span class="icon-[material-symbols--add]"></span>Neue Auslandsbeschäftigung
                                    hinzufügen
                                  </button>
                                `
                              : nothing
                          }
                        </div>
                      `
                    : nothing
                }

                <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">Rentenversicherung</h4>
                <p class="col-span-full text-base-content/70">
                  Der Arbeitgeber trägt Pauschalbeiträge zur Rentenversicherung in Höhe von 15 %. Der Arbeitnehmer trägt
                  die Differenz zum vollen Beitragssatz in der Rentenversicherung (18,6%). Den Arbeitnehmeranteil am
                  Beitrag zur Rentenversicherung zieht der Arbeitgeber vom Arbeitsentgelt ab und leitet diesen mit
                  seinen Abgaben an die Minijob-Zentrale weiter.
                </p>
                <p class="col-span-full text-base-content/70">
                  Der Arbeitgeber zahlt Pauschalbeiträge. Die einmal beantragte Befreiung von der
                  Rentenversicherungspflicht kann nicht rückgängig gemacht werden.
                </p>
                ${FormGridEntry({
                  label: 'Befreiung der Rentenversicherung',
                  render: CheckboxInput({
                    ...props.form.field(
                      'mitarbeiterDaten',
                      'anGeringfuegigeBeschaeftigung',
                      'beantrageBefreiungRentenversicherung',
                    ),
                    onPicked: value => {
                      props.form
                        .field(
                          'mitarbeiterDaten',
                          'anGeringfuegigeBeschaeftigung',
                          'beantrageBefreiungRentenversicherung',
                        )
                        .onPicked?.(value);
                      manuallyUpdateForm();
                    },
                  }),
                })}
                <p class="col-span-full text-sm text-base-content/70">
                  ${
                    anGB?.beantrageBefreiungRentenversicherung
                      ? 'Ja, ich möchte mich von der Versicherungspflicht in der Rentenversicherung befreien lassen.'
                      : 'Nein, ich möchte mich von der Versicherungspflicht in der Rentenversicherung nicht befreien lassen.'
                  }
                </p>
                ${
                  anGB?.beantrageBefreiungRentenversicherung
                    ? html`
                        ${FormGridEntry({
                          label: 'Altersvollrentner',
                          render: CheckboxInput({
                            ...props.form.field(
                              'mitarbeiterDaten',
                              'anGeringfuegigeBeschaeftigung',
                              'istAltersvollrentner',
                            ),
                          }),
                        })}
                        <p class="col-span-full text-sm text-base-content/70">
                          Ich bin Altersvollrentner nach Erreichen der Regelaltersgrenze bzw. Versorgungsempfänger nach
                          Erreichen einer Altersgrenze und rentenversicherungsfrei. Eine Befreiung von der
                          Versicherungspflicht in der Rentenversicherung ist deshalb nicht erforderlich.
                        </p>
                      `
                    : nothing
                }
              `
            : nothing
        }
      </div>
    `;
  },
);

export const EmployeeSectionGeringfuegigeBeschaeftigungFormValidator = {
  pauschalbesteuerungGeringfuegig: options => {
    if (options.target.geringfuegigeBeschaeftigungGewuenscht) {
      return sgNonOptional({ allowFalsy: true })(options);
    }
  },
  befreiungRentenversicherungspflichtGewuenscht: options => {
    if (options.target.geringfuegigeBeschaeftigungGewuenscht) {
      return sgNonOptional({ allowFalsy: true })(options);
    }
  },
  arbeits: {
    endeBeschaftigungMeldung: options => {
      if (!options.value || !options.target.beginBeschaftigungMeldung) return;
      if (options.value <= options.target.beginBeschaftigungMeldung)
        throw new Error('Ende der Beschäftigung muss nach Beginn liegen.');
    },
  },
  auslandbeschaeftigung: {
    beschaftigungsEnde: options => {
      if (!options.value || !options.target.beschaftigungsBeginn) return;
      if (options.value <= options.target.beschaftigungsBeginn)
        throw new ValidationError('Ende der Beschäftigung muss nach Beginn liegen.', {
          key: options.key,
          target: options.target,
          path: options.path,
        });
    },
  },
  beschaeftigungen: {
    lohn: options => {
      if (options.target.geringfuegigeBeschaeftigungGewuenscht) {
        return sgNonOptional({ allowFalsy: true })(options);
      }
    },
  },
};
